import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Shield, RefreshCw, Eye, FileText, AlertCircle, CheckCircle, Clock,
  MessageCircle, Send, ClipboardList, Paperclip, Calendar, UserX, Users as UsersIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMBITI_LABEL = {
  corruzione: "Corruzione",
  frode: "Frode",
  discriminazione: "Discriminazione",
  molestie: "Molestie",
  violazioni_gdpr: "Violazioni GDPR",
  sicurezza_lavoro: "Sicurezza lavoro",
  antiriciclaggio: "Antiriciclaggio",
  violazioni_231: "Violazioni 231",
  violazioni_ivass: "Violazioni IVASS",
  ambiente: "Ambiente",
  concorrenza_sleale: "Concorrenza sleale",
  codice_etico: "Codice Etico",
  altro: "Altro",
};

const STATI = [
  { value: "ricevuta", label: "Ricevuta", icon: AlertCircle, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "presa_in_carico", label: "Presa in carico", icon: ClipboardList, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "in_esame", label: "In esame", icon: Clock, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "approfondimento", label: "Approfondimento", icon: FileText, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "archiviata", label: "Archiviata", icon: UserX, color: "bg-zinc-50 text-zinc-600 border-zinc-200" },
  { value: "risolta", label: "Risolta", icon: CheckCircle, color: "bg-green-50 text-green-700 border-green-200" },
  { value: "trasmessa_anac", label: "Trasmessa ANAC", icon: Shield, color: "bg-red-50 text-red-700 border-red-200" },
];
const STATO_CONF = (v) => STATI.find(s => s.value === v) || STATI[0];

function daysLeft(isoDate) {
  if (!isoDate) return null;
  const diff = Math.ceil((new Date(isoDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function WhistleblowingAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/whistleblowing`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento segnalazioni");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDetail = async (item) => {
    try {
      const { data } = await axios.get(`${API}/whistleblowing/${item.id}`, { withCredentials: true });
      setSelected(data);
      setDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

  if (loading) return <div className="bg-white rounded-xl border border-zinc-200 p-8 text-center text-zinc-400">Caricamento...</div>;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2" data-testid="wb-admin-title">
            <Shield className="w-5 h-5 text-[#163321]" /> Canale Whistleblowing
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">Accesso riservato al Gestore del Canale — D.Lgs. 24/2023</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchItems} className="h-9 text-xs" data-testid="wb-admin-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        {[
          { l: "Totale", v: items.length, c: "text-zinc-600", bg: "bg-zinc-100" },
          { l: "Da esaminare", v: items.filter(i => i.stato === "ricevuta").length, c: "text-amber-600", bg: "bg-amber-50" },
          { l: "In corso", v: items.filter(i => ["presa_in_carico", "in_esame", "approfondimento"].includes(i.stato)).length, c: "text-blue-600", bg: "bg-blue-50" },
          { l: "Chiuse", v: items.filter(i => ["archiviata", "risolta", "trasmessa_anac"].includes(i.stato)).length, c: "text-green-600", bg: "bg-green-50" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-xs text-zinc-500">{s.l}</p>
            <p className={`text-xl font-heading font-bold ${s.c} mt-1`}>{s.v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-zinc-50/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Ticket</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Ambito</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Modalita'</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Ricevuta</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Scadenza 7gg</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Scadenza 3m</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Stato</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-zinc-400">Nessuna segnalazione ricevuta</td></tr>
              ) : items.map(it => {
                const conf = STATO_CONF(it.stato);
                const d7 = daysLeft(it.scadenza_avviso_7gg);
                const d3m = daysLeft(it.scadenza_riscontro_90gg);
                return (
                  <tr key={it.id} className="border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`wb-row-${it.ticket_code}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#163321]">{it.ticket_code}</td>
                    <td className="px-4 py-3 text-xs">{AMBITI_LABEL[it.ambito] || it.ambito}</td>
                    <td className="px-4 py-3 text-xs">
                      {it.anonimo
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600"><UserX className="w-3 h-3" /> Anonima</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700"><UsersIcon className="w-3 h-3" /> Identificata</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(it.created_at)}</td>
                    <td className={`px-4 py-3 text-xs ${d7 !== null && d7 < 2 ? "text-red-600 font-semibold" : "text-zinc-500"}`}>
                      {d7 !== null ? (d7 >= 0 ? `${d7} gg` : "Scaduto") : "-"}
                    </td>
                    <td className={`px-4 py-3 text-xs ${d3m !== null && d3m < 15 ? "text-red-600 font-semibold" : "text-zinc-500"}`}>
                      {d3m !== null ? (d3m >= 0 ? `${d3m} gg` : "Scaduto") : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${conf.color}`}>
                        <conf.icon className="w-3 h-3" />{conf.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(it)} className="text-zinc-500 hover:text-zinc-900 text-xs h-7 px-2" data-testid={`wb-view-${it.ticket_code}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="wb-detail-dialog">
          {selected && <WhistleblowingDetail item={selected} onUpdated={async () => { await fetchItems(); const { data } = await axios.get(`${API}/whistleblowing/${selected.id}`, { withCredentials: true }); setSelected(data); }} fmtDate={fmtDate} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function WhistleblowingDetail({ item, onUpdated, fmtDate }) {
  const [stato, setStato] = useState(item.stato);
  const [rispostaPubblica, setRispostaPubblica] = useState(item.risposta_pubblica || "");
  const [messaggio, setMessaggio] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  const conf = STATO_CONF(item.stato);

  const updateStato = async () => {
    if (stato === item.stato) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/whistleblowing/${item.id}`, { stato }, { withCredentials: true });
      toast.success("Stato aggiornato");
      await onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const saveRispostaPubblica = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/whistleblowing/${item.id}`, { risposta_pubblica: rispostaPubblica }, { withCredentials: true });
      toast.success("Risposta pubblica salvata (visibile al segnalante)");
      await onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const sendMessaggio = async () => {
    if (!messaggio.trim()) return toast.error("Inserisci un messaggio");
    setSaving(true);
    try {
      await axios.patch(`${API}/whistleblowing/${item.id}`, { messaggio_al_segnalante: messaggio }, { withCredentials: true });
      toast.success("Messaggio inviato al segnalante");
      setMessaggio("");
      await onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const saveNota = async () => {
    if (!nota.trim()) return toast.error("Inserisci una nota");
    setSaving(true);
    try {
      await axios.patch(`${API}/whistleblowing/${item.id}`, { nota }, { withCredentials: true });
      toast.success("Nota interna registrata");
      setNota("");
      await onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#163321]" />
          <span className="font-mono">{item.ticket_code}</span>
          <span className={`ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${conf.color}`}>
            <conf.icon className="w-3 h-3" />{conf.label}
          </span>
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 mt-4 text-sm">
        {/* META */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-zinc-50 rounded-xl p-4">
          <div><span className="text-zinc-500 text-[10px] uppercase">Ambito</span><p className="font-medium">{AMBITI_LABEL[item.ambito] || item.ambito}</p></div>
          <div><span className="text-zinc-500 text-[10px] uppercase">Ricevuta</span><p>{fmtDate(item.created_at)}</p></div>
          <div><span className="text-zinc-500 text-[10px] uppercase">Avviso 7gg</span><p>{fmtDate(item.scadenza_avviso_7gg)}</p></div>
          <div><span className="text-zinc-500 text-[10px] uppercase">Riscontro 3m</span><p>{fmtDate(item.scadenza_riscontro_90gg)}</p></div>
        </div>

        {/* CONTATTO */}
        {item.anonimo ? (
          <div className="bg-zinc-50 rounded-xl p-4 text-xs text-zinc-600 flex items-center gap-2">
            <UserX className="w-4 h-4" /> Segnalazione <strong>anonima</strong>. Nessun dato identificativo del segnalante.
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-[10px] uppercase text-blue-700 mb-2">Contatto segnalante (riservato)</p>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><span className="text-zinc-500">Nome</span><p className="font-medium">{item.contatto?.nome || "-"}</p></div>
              <div><span className="text-zinc-500">Email</span><p className="font-medium">{item.contatto?.email || "-"}</p></div>
              <div><span className="text-zinc-500">Telefono</span><p className="font-medium">{item.contatto?.telefono || "-"}</p></div>
            </div>
          </div>
        )}

        {/* RICHIESTA INCONTRO */}
        {item.richiesta_incontro && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-purple-900 flex items-center gap-1"><Calendar className="w-4 h-4" /> Richiesta di incontro diretto</p>
            {item.note_incontro && <p className="text-xs text-purple-800 mt-2">{item.note_incontro}</p>}
          </div>
        )}

        {/* DESCRIZIONE */}
        <div>
          <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Descrizione dei fatti</p>
          <div className="bg-white border border-zinc-200 rounded-xl p-4 whitespace-pre-wrap text-[13px] leading-relaxed">
            {item.descrizione}
          </div>
        </div>

        {/* SOGGETTI LUOGO PERIODO */}
        {(item.soggetti || item.luogo || item.periodo) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            {item.luogo && <div><span className="text-zinc-500 uppercase text-[10px]">Luogo</span><p className="font-medium">{item.luogo}</p></div>}
            {item.periodo && <div><span className="text-zinc-500 uppercase text-[10px]">Periodo</span><p className="font-medium">{item.periodo}</p></div>}
            {item.soggetti && <div className="col-span-full"><span className="text-zinc-500 uppercase text-[10px]">Soggetti coinvolti</span><p className="whitespace-pre-wrap">{item.soggetti}</p></div>}
          </div>
        )}

        {/* ALLEGATI */}
        {item.file_ids?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Allegati ({item.file_ids.length})</p>
            <div className="flex flex-wrap gap-2">
              {item.file_ids.map(fid => (
                <a key={fid} href={`${API}/files/${fid}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs transition-colors">
                  <Paperclip className="w-3.5 h-3.5" /> {fid.slice(0, 8)}...
                </a>
              ))}
            </div>
          </div>
        )}

        {/* CONVERSAZIONE */}
        <div>
          <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2 flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" /> Conversazione col segnalante ({item.messaggi?.length || 0})
          </p>
          {item.messaggi?.length > 0 ? (
            <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
              {item.messaggi.map((m, i) => (
                <div key={i} className={`flex ${m.autore_tipo === "gestore" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-xl p-3 ${m.autore_tipo === "gestore" ? "bg-[#163321] text-white" : "bg-zinc-100 text-zinc-900"}`}>
                    <p className="text-[10px] uppercase opacity-70 mb-1">{m.autore_tipo === "gestore" ? "Tu (Gestore)" : "Segnalante"} — {fmtDate(m.data)}</p>
                    <p className="text-xs whitespace-pre-wrap">{m.testo}</p>
                    {m.file_ids?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.file_ids.map(fid => (
                          <a key={fid} href={`${API}/files/${fid}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-[10px]">
                            <Paperclip className="w-3 h-3" /> Allegato
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-zinc-400 italic mb-3">Nessun messaggio scambiato.</p>
          )}
          <div className="flex gap-2">
            <Textarea value={messaggio} onChange={(e) => setMessaggio(e.target.value)} placeholder="Scrivi un messaggio al segnalante..." rows={2} maxLength={5000} className="flex-1 rounded-xl border-zinc-200 resize-none text-xs" data-testid="wb-admin-new-message" />
            <Button onClick={sendMessaggio} disabled={saving} className="bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-xl h-auto" data-testid="wb-admin-send-message">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* RISPOSTA PUBBLICA (riscontro ufficiale) */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-amber-900 mb-2">Riscontro ufficiale (art. 5 c.1 lett. d)</p>
          <Textarea value={rispostaPubblica} onChange={(e) => setRispostaPubblica(e.target.value)} placeholder="Riscontro sull'esito delle verifiche, visibile al segnalante..." rows={3} maxLength={5000} className="rounded-xl border-amber-200 resize-none text-xs bg-white" data-testid="wb-admin-risposta" />
          <Button size="sm" onClick={saveRispostaPubblica} disabled={saving} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-full" data-testid="wb-admin-save-risposta">
            Salva riscontro
          </Button>
        </div>

        {/* NOTE INTERNE */}
        <div>
          <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Note interne (non visibili al segnalante)</p>
          {item.note_interne?.length > 0 && (
            <div className="space-y-2 mb-3">
              {item.note_interne.map((n, i) => (
                <div key={i} className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  <p className="text-[10px] text-yellow-800">{n.autore} — {fmtDate(n.data)}</p>
                  <p className="text-xs text-yellow-900 whitespace-pre-wrap mt-1">{n.testo}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota interna del gestore..." rows={2} maxLength={2000} className="flex-1 rounded-xl border-zinc-200 resize-none text-xs" data-testid="wb-admin-new-nota" />
            <Button size="sm" onClick={saveNota} disabled={saving} variant="outline" className="rounded-xl" data-testid="wb-admin-save-nota">Aggiungi</Button>
          </div>
        </div>

        {/* CAMBIO STATO */}
        <div className="flex items-end gap-2 pt-4 border-t border-zinc-200">
          <div className="flex-1">
            <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-1">Cambia stato</p>
            <Select value={stato} onValueChange={setStato}>
              <SelectTrigger className="rounded-lg" data-testid="wb-admin-stato-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={updateStato} disabled={saving || stato === item.stato} className="bg-[#163321] text-white rounded-lg" data-testid="wb-admin-save-stato">
            Aggiorna
          </Button>
        </div>

        {/* AUDIT LOG */}
        {item.audit_log?.length > 0 && (
          <details className="bg-zinc-50 rounded-xl p-3">
            <summary className="text-[10px] uppercase text-zinc-500 font-semibold cursor-pointer">Audit log ({item.audit_log.length} eventi)</summary>
            <ul className="mt-2 space-y-1 text-[11px] text-zinc-600 font-mono max-h-48 overflow-y-auto">
              {item.audit_log.map((a, i) => (
                <li key={i}>{fmtDate(a.timestamp)} — <strong>{a.evento}</strong> [{a.autore}] {a.dettagli && `· ${a.dettagli}`}</li>
              ))}
            </ul>
          </details>
        )}

        {/* RETENTION */}
        <p className="text-[10px] text-zinc-400 text-center pt-2">
          Conservazione massima: {fmtDate(item.scadenza_conservazione)} (art. 14 D.Lgs. 24/2023 — 5 anni)
        </p>
      </div>
    </>
  );
}
